import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutGrid, Activity, Radar, Wallet, Network, Sparkles,
    KeyRound, UserPlus, ShieldCheck, LogOut, Crown,
} from "lucide-react";

const COMMANDER_AVATAR =
    "https://static.prod-images.emergentagent.com/jobs/8041819d-7954-4bde-9fe6-e6847159f659/images/bbe1b4298714d02fd8782200fa5bc790dafd0d282dfba83a22d92a574fb758f6.png";

const NAV = [
    { to: "/", label: "Command", icon: LayoutGrid, testId: "nav-command", end: true },
    { to: "/hud", label: "Campaign HUD", icon: Activity, testId: "nav-hud" },
    { to: "/sentry", label: "Lead Sentry Gold", icon: Radar, testId: "nav-sentry" },
    { to: "/wallet", label: "60/30/10 Wallet", icon: Wallet, testId: "nav-wallet" },
    { to: "/bridge", label: "Cross-Fleet Bridge", icon: Network, testId: "nav-bridge" },
    { to: "/signals", label: "Brand Signals", icon: Sparkles, testId: "nav-signals" },
    { to: "/vault", label: "API Vault", icon: KeyRound, testId: "nav-vault" },
    { to: "/onboard", label: "Agent Onboarding", icon: UserPlus, testId: "nav-onboard" },
];

export function Panel({ title, tag, action, children, className = "", testId }) {
    return (
        <section className={`hud-panel p-5 lg:p-6 ${className}`} data-testid={testId}>
            <div className="hud-corner-tl" />
            <div className="hud-corner-tr" />
            <div className="hud-corner-bl" />
            <div className="hud-corner-br" />
            {(title || tag || action) && (
                <header className="flex items-start justify-between mb-4 relative z-10 gap-3">
                    <div>
                        {tag && <div className="label-tag mb-1">{tag}</div>}
                        {title && <h2 className="heading-display text-lg sm:text-xl text-white">{title}</h2>}
                    </div>
                    {action && <div>{action}</div>}
                </header>
            )}
            <div className="circuit-divider mb-4 relative z-10" />
            <div className="relative z-10">{children}</div>
        </section>
    );
}

export default function Layout() {
    const nav = useNavigate();
    const loc = useLocation();

    const logout = () => {
        localStorage.removeItem("eigoo_anchor");
        nav("/anchor");
    };

    const activeTitle =
        NAV.find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to)))?.label || "Command";

    return (
        <div className="min-h-screen flex bg-obsidian text-white" data-testid="shell-root">
            <aside
                className="w-64 shrink-0 bg-[#070707] border-r border-[#332A0C] flex flex-col"
                data-testid="sidebar"
            >
                <div className="p-5 border-b border-[#332A0C]">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 border border-gold flex items-center justify-center pulse-gold">
                            <Crown className="w-4 h-4 text-gold" />
                        </div>
                        <div>
                            <div className="heading-display text-base leading-none">EIGOO</div>
                            <div className="label-tag text-[9px] mt-1">Digital Marketing</div>
                        </div>
                    </div>
                    <div className="mt-4 text-[9px] tracking-[0.28em] text-gold/70 uppercase mono">
                        marketing.eigooinc.net
                    </div>
                </div>

                <nav className="flex-1 py-3" data-testid="primary-nav">
                    {NAV.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={item.testId}
                                className={({ isActive }) =>
                                    `group flex items-center gap-3 px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] border-l-2 transition-all ${
                                        isActive
                                            ? "border-gold bg-[#0e0e0e] text-gold"
                                            : "border-transparent text-[#888] hover:text-white hover:border-[#332A0C] hover:bg-[#0a0a0a]"
                                    }`
                                }
                            >
                                <Icon className="w-4 h-4" strokeWidth={1.5} />
                                <span>{item.label}</span>
                            </NavLink>
                        );
                    })}
                    <NavLink
                        to="/anchor"
                        data-testid="nav-anchor"
                        className="group flex items-center gap-3 px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] border-l-2 border-transparent text-[#888] hover:text-gold hover:border-[#332A0C]"
                    >
                        <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                        <span>Identity Anchor</span>
                    </NavLink>
                </nav>

                <div className="p-4 border-t border-[#332A0C] space-y-3">
                    <div className="px-2 py-2 border border-gold-edge mono text-[9px] tracking-[0.24em] uppercase text-gold/70 text-center">
                        Heir › Ashairah
                    </div>
                    <button
                        onClick={logout}
                        data-testid="logout-btn"
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-[#888] hover:text-gold transition-colors border border-gold-edge hover:border-gold"
                    >
                        <LogOut className="w-3 h-3" /> Disengage
                    </button>
                </div>
            </aside>

            <div className="flex-1 flex flex-col min-w-0">
                <header
                    className="h-16 px-8 flex items-center justify-between border-b border-[#332A0C] bg-black/60 backdrop-blur-xl"
                    data-testid="topbar"
                >
                    <div className="flex items-center gap-4">
                        <div className="pulse-dot" />
                        <div className="label-tag" data-testid="topbar-section">
                            SECTOR // {activeTitle}
                        </div>
                    </div>
                    <div className="flex items-center gap-5">
                        <div className="hidden md:flex items-center gap-2 text-xs">
                            <span className="label-tag text-[#888]">CLEARANCE</span>
                            <span className="mono text-gold font-bold">OMEGA-GOLD</span>
                        </div>
                        <div className="hidden md:block h-6 w-px bg-[#332A0C]" />
                        <div className="flex items-center gap-3" data-testid="commander-badge">
                            <div className="text-right hidden sm:block">
                                <div className="text-xs font-bold uppercase tracking-wider">Admiral Akanksha</div>
                                <div className="label-tag text-[#888]">Sovereign Commander</div>
                            </div>
                            <div className="w-10 h-10 border border-gold overflow-hidden">
                                <img
                                    src={COMMANDER_AVATAR}
                                    alt="Commander Akanksha"
                                    className="w-full h-full object-cover"
                                    data-testid="admiral-avatar"
                                />
                            </div>
                        </div>
                    </div>
                </header>

                <main className="flex-1 p-6 lg:p-8 overflow-y-auto" data-testid="main-content">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
